import { useTranslation } from "react-i18next";
import { Smartphone } from "lucide-react";
import { appLegalUrl } from "@/utils/appLegal";

// Bidmio is on a phone three ways, all live: the App Store app, the Google
// Play app and the installable web app (PWA). Copy lives here (like
// PeppolTrustBadge) so the footer does not touch the shared locale seeds.
// The listing URLs match the app repo's core.usecases.mobile.release_metadata.
export const APP_STORE_URL = "https://apps.apple.com/app/id1673144077";
export const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.bidmio.app";

const COPY = {
  sk: {
    title: "Bidmio vo vašom mobile",
    note: "Pre iPhone, Android alebo ako webová aplikácia priamo z prehliadača.",
    appStore: "Stiahnuť z",
    googlePlay: "Získajte to na",
    aria: "Stiahnite si Bidmio z App Store alebo Google Play, alebo si ho nainštalujte ako webovú aplikáciu",
    pwaEyebrow: "Bez obchodu · PWA",
    pwaCta: "Pridať na plochu",
  },
  cs: {
    title: "Bidmio ve vašem mobilu",
    note: "Pro iPhone, Android nebo jako webová aplikace přímo z prohlížeče.",
    appStore: "Stáhnout z",
    googlePlay: "Nyní na",
    aria: "Stáhněte si Bidmio z App Storu nebo Google Play, nebo si ho nainstalujte jako webovou aplikaci",
    pwaEyebrow: "Bez obchodu · PWA",
    pwaCta: "Přidat na plochu",
  },
  da: {
    title: "Bidmio på din telefon",
    note: "Til iPhone, Android eller som webapp direkte fra browseren.",
    appStore: "Hent i",
    googlePlay: "Hent den på",
    aria: "Hent Bidmio i App Store eller på Google Play, eller installer den som webapp",
    pwaEyebrow: "Uden app-butik · PWA",
    pwaCta: "Føj til startskærm",
  },
  en: {
    title: "Bidmio on your phone",
    note: "For iPhone, Android or as a web app straight from your browser.",
    appStore: "Download on the",
    googlePlay: "Get it on",
    aria: "Download Bidmio from the App Store or Google Play, or install it as a web app",
    pwaEyebrow: "No app store · PWA",
    pwaCta: "Add to home screen",
  },
  de: {
    title: "Bidmio auf Ihrem Handy",
    note: "Für iPhone, Android oder als Web-App direkt aus dem Browser.",
    appStore: "Laden im",
    googlePlay: "Jetzt bei",
    aria: "Laden Sie Bidmio aus dem App Store oder von Google Play, oder installieren Sie es als Web-App",
    pwaEyebrow: "Ohne App-Store · PWA",
    pwaCta: "Zum Startbildschirm",
  },
} as const;

type Lang = keyof typeof COPY;

interface MobileAppLinksProps {
  className?: string;
}

const tile =
  "flex h-full min-h-[5.5rem] items-center rounded-xl border p-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-background";

const MobileAppLinks = ({ className = "" }: MobileAppLinksProps) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language in COPY ? i18n.language : "en") as Lang;
  const copy = COPY[lang];
  // The stores' own badges, unchanged, as their brand guidelines require.
  // Google Play ships one per language; Apple's is the generic English one.
  const stores = [
    { href: APP_STORE_URL, alt: `${copy.appStore} App Store`, badge: "/images/app-badges/app-store.svg", width: 156, testId: "app-store-link" },
    {
      href: GOOGLE_PLAY_URL, alt: `${copy.googlePlay} Google Play`, badge: `/images/app-badges/google-play-${lang}.png`,
      width: 175, testId: "google-play-link",
    },
  ];

  // One band: heading on the left, then App Store, Google Play and the web app as equal tiles.
  return (
    <section
      aria-label={copy.aria}
      data-testid="mobile-app-links"
      className={`grid gap-3 rounded-2xl border border-background/20 p-4 md:grid-cols-3 md:gap-4 md:p-6 lg:grid-cols-[1.1fr_repeat(3,minmax(0,1fr))] ${className}`}
    >
      <div className="flex flex-col justify-center gap-1 md:col-span-3 lg:col-span-1 lg:pr-2">
        <h3 className="text-lg font-semibold text-background">{copy.title}</h3>
        <p className="text-sm text-background/70">{copy.note}</p>
      </div>
      {stores.map(({ href, alt, badge, width, testId }) => (
        <a
          key={testId}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testId}
          className={`${tile} justify-center border-background/20 hover:border-background/40 hover:bg-background/[0.04]`}
        >
          <img src={badge} alt={alt} width={width} height={52} loading="lazy" className="h-[52px] w-auto" />
        </a>
      ))}
      <a
        href={appLegalUrl(lang, "/install-app")}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="app-pwa-install-link"
        className={`${tile} gap-3 border-dashed border-background/40 bg-background/[0.08] hover:bg-background/[0.12]`}
      >
        <Smartphone className="h-7 w-7 shrink-0 text-background" aria-hidden="true" />
        <span className="flex flex-col leading-tight">
          <span className="text-[11px] uppercase tracking-wide text-background/80">{copy.pwaEyebrow}</span>
          <span className="text-lg font-semibold text-background">{copy.pwaCta}</span>
        </span>
      </a>
    </section>
  );
};

export default MobileAppLinks;
